import { type ReactFormExtendedApi, useForm } from "@tanstack/react-form";
import { useNavigate } from "@tanstack/react-router";
import React from "react";

import { createNew } from "@blis/shared/schemas/accession";
import {
  isCaseTypeMissingWhenRequired,
  parseCaseTypeRequiredOrgIds,
} from "@blis/shared/schemas/settings";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import type { FormProps } from "@/components/ui/form";
import { toast } from "@/components/ui/toast";
import { apiUtils } from "@/context/query";
import { useSettings } from "@/features/admin/hooks";
import type { TSubjectAccession } from "@/lib/types";
import { guessTimezone } from "@/lib/utils/tz";
import { useFormReceivingOrg } from "../form/common";
import type { FormInput, FormValues } from "./schema";

export type PreferredTube = RouterOutputs["tests"]["preferredTubes"][0];
type Instruction = FormValues["instructions"][0];

export type AccessionFormProps = FormProps & {
  children: React.ReactNode;
  subjectId?: number;
  orgId?: number | null;
  containerRef?: React.RefObject<HTMLElement | null>;
  onCreated?: (accessionId: number) => void;
};

type AccessionFormContextValue = {
  form: ReactFormExtendedApi<FormInput>;

  preferredTubes: PreferredTube[];
  setPreferredTubes: (instructions: Instruction[]) => void;

  accessions: TSubjectAccession[];
  setAccessions: (subjectId: number) => void;

  ignoredPreferredTubeIds: number[];
  setIgnoredPreferredTubeIds: React.Dispatch<React.SetStateAction<number[]>>;
};

const AccessionFormContext = React.createContext(
  {} as AccessionFormContextValue,
);

export function AccessionFormProvider({
  children,
  subjectId,
  orgId,
  containerRef,
  onCreated,
}: AccessionFormProps) {
  const [accessions, setAccessions] = React.useState<TSubjectAccession[]>([]);
  const [preferredTubes, setPreferredTubes] = React.useState<PreferredTube[]>(
    [],
  );
  const [ignoredPreferredTubeIds, setIgnoredPreferredTubeIds] = React.useState<
    number[]
  >([]);

  const setPreferredTubesFromInstructions = (instructions: Instruction[]) => {
    if (instructions.length === 0) {
      setPreferredTubes([]);
      return;
    }
    apiUtils.tests.preferredTubes
      .fetch({ instructions }, { staleTime: 600_000 })
      .then(setPreferredTubes);
  };

  const setAccessionsFromSubjectId = (subjectId: number) => {
    apiUtils.subjects.accessions.fetch(subjectId).then(setAccessions);
  };

  const receivingOrg = useFormReceivingOrg();
  const { settings } = useSettings();
  const requiredOrgIdsRef = React.useRef(
    parseCaseTypeRequiredOrgIds(settings.caseTypeRequiredOrgIds),
  );
  requiredOrgIdsRef.current = parseCaseTypeRequiredOrgIds(
    settings.caseTypeRequiredOrgIds,
  );

  React.useEffect(() => {
    if (preferredTubes.length === 0) {
      setIgnoredPreferredTubeIds([]);
    }
  }, [preferredTubes]);

  const navigate = useNavigate();
  const create = trpc.accession.createForSubject.useMutation();

  const form = useForm({
    defaultValues: {
      subjectId: subjectId ?? Number.NaN,
      orgId: orgId ?? Number.NaN,
      caseType: undefined,
      receivingOrgId: receivingOrg ?? Number.NaN,
      collectTimezone: guessTimezone().zone,
      trackingId: "",
      requisitionId: "",
    },
    validators: {
      onSubmit: createNew.superRefine((value, ctx) => {
        if (
          isCaseTypeMissingWhenRequired(
            value.orgId,
            value.caseType,
            requiredOrgIdsRef.current,
          )
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["caseType"],
            message: "Case type is required",
          });
        }
      }),
    },
    onSubmit: async ({ value }) => {
      await create.mutateAsync(value, {
        onSuccess: (data) => {
          if (data.input.createAnother) {
            form.reset({
              packedAt: data.input.packedAt,
              collectTimezone: data.input.collectTimezone,

              subjectId: data.input.subjectId,
              orgId: data.input.orgId,
              caseType: data.input.caseType,
              receivingOrgId: data.input.receivingOrgId,

              sharedOrgs: data.input.sharedOrgs,
              additionalEmails: data.input.additionalEmails,

              samples: [],
              instructions: [],

              priority: "ROUTINE",
              trackingId: "",
              requisitionId: "",

              createAnother: true,
            });
            setPreferredTubes([]);
            if (containerRef?.current) {
              containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
            }
            toast.success("Accession created", {
              action: {
                label: "View",
                onClick: () => {
                  navigate({
                    to: "/accessions/$id",
                    params: { id: data.accessionId },
                  });
                },
              },
            });
          } else {
            if (onCreated) {
              onCreated(data.accessionId);
            } else {
              navigate({
                to: "/accessions/$id",
                params: { id: data.accessionId },
              });
            }
          }
        },
        onError: (err) => {
          toast.error("Error creating accession", {
            description: err.message,
          });
        },
      });
    },
  });

  React.useEffect(() => {
    if (receivingOrg) {
      form.setFieldValue("receivingOrgId", receivingOrg);
    }
  }, [receivingOrg, form.setFieldValue]);

  return (
    <AccessionFormContext.Provider
      value={{
        form,
        accessions,
        setAccessions: setAccessionsFromSubjectId,
        preferredTubes,
        setPreferredTubes: setPreferredTubesFromInstructions,
        ignoredPreferredTubeIds,
        setIgnoredPreferredTubeIds,
      }}
    >
      {children}
    </AccessionFormContext.Provider>
  );
}

export function useAccessionForm() {
  const ctx = React.useContext(AccessionFormContext);
  if (!ctx) {
    throw new Error(
      "useAccessionForm must be used within AccessionFormProvider",
    );
  }
  return ctx;
}
